import Logger from '@/services/Logger';
import { useEffect, useRef } from 'react';

type Options = {
    // How many clicks within the window are considered aggressive
    threshold?: number;
    // Time window in milliseconds to count the clicks
    windowMs?: number;
    // Minimum time between two logs so the SEQ logger is not flooded
    cooldownMs?: number;
};

/**
 * Detects "aggressive" clicking behavior (many rapid clicks in a short window)
 * and reports it to the SEQ logger. Useful to spot frustrated users or pages
 * that feel unresponsive.
 */
export default ({ threshold = 5, windowMs = 1000, cooldownMs = 5000 }: Options = {}) => {
    const timestampsRef = useRef<number[]>([]);
    const lastLoggedAtRef = useRef<number>(0);

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            const now = Date.now();

            // Keep only the clicks that happened inside the time window
            timestampsRef.current = timestampsRef.current.filter((ts) => now - ts <= windowMs);
            timestampsRef.current.push(now);

            if (timestampsRef.current.length < threshold) return;

            // Respect a cooldown so a single burst is not logged repeatedly
            if (now - lastLoggedAtRef.current < cooldownMs) return;

            lastLoggedAtRef.current = now;

            const target = event.target as HTMLElement | null;

            Logger.startSession({
                'Behavior.Type': 'AggressiveClicking',
                'Behavior.ClickCount': timestampsRef.current.length,
                'Behavior.WindowMs': windowMs,
            });

            Logger.log_warning(`Aggressive clicking detected: ${timestampsRef.current.length} clicks within ${windowMs}ms on page ${window.location.pathname}`, {
                path: window.location.pathname,
                clickCount: timestampsRef.current.length,
                windowMs,
                targetTag: target?.tagName ?? '',
                targetText: target?.innerText?.slice(0, 80) ?? '',
            });

            Logger.endSession();

            // Reset so the next burst is measured from scratch
            timestampsRef.current = [];
        };

        window.addEventListener('click', handleClick);

        return () => {
            window.removeEventListener('click', handleClick);
        };
    }, [threshold, windowMs, cooldownMs]);
};
